import React from 'react';
import { motion } from 'framer-motion';
import { MatchRoomMetadata, Currency } from '@solante/shared';
import { formatDistanceToNow } from 'date-fns';
import { formatLamports, normalizeLamports, shortKey } from '../../utils/format';
import { MatchCardSkeleton } from './skeletons';

interface Props {
  matches?: MatchRoomMetadata[];
  loading?: boolean;
  emptyMessage?: string;
  onJoin?: (match: MatchRoomMetadata) => void;
  isJoining?: boolean;
  joiningMatchId?: string | null;
  escrowStatus?: Record<
    string,
    {
      locked: number;
      total: number;
      wallets: string[];
      status: MatchRoomMetadata['status'];
    }
  >;
}

const currencyLabel: Record<Currency, string> = {
  SOL: 'SOL',
  USDC: 'USDC',
};

const statusCopy: Record<MatchRoomMetadata['status'], string> = {
  open: 'Open',
  awaitingSecondPlayer: 'Waiting for opponent',
  inProgress: 'In progress',
  settling: 'Settling',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const MatchList: React.FC<Props> = ({
  matches,
  loading,
  emptyMessage,
  onJoin,
  isJoining,
  joiningMatchId,
  escrowStatus,
}) => {
  if (loading) {
    return <MatchCardSkeleton count={3} />;
  }

  if (!matches?.length) {
    return <p className="text-sm text-white/50">{emptyMessage}</p>;
  }

  return (
    <ul className="space-y-3 text-sm text-white/80">
      {matches.map((match) => {
        const escrow = escrowStatus?.[match.matchId];
        const presumedLocked =
          match.status === 'awaitingSecondPlayer' ? 1 : match.status === 'inProgress' ? 2 : 0;
        const total = escrow?.total ?? 2;
        const locked = Math.min(escrow?.locked ?? presumedLocked, total);
        const walletSummary = escrow?.wallets?.length
          ? escrow.wallets.map((wallet) => shortKey(wallet)).join(', ')
          : locked > 0
            ? 'Awaiting opponent stake'
            : 'Awaiting stakes';
        const statusLabel = statusCopy[escrow?.status ?? match.status] ?? statusCopy.open;
        const expiresLabel = formatDistanceToNow(new Date(match.expiresAt * 1000), {
          addSuffix: true,
        });
        const joiningThisMatch = Boolean(isJoining && joiningMatchId === match.matchId);
        const disableButton = !onJoin || match.wager.currency !== 'SOL' || Boolean(isJoining);
        const joinLabel =
          match.wager.currency === 'SOL'
            ? joiningThisMatch
              ? 'Connecting…'
              : 'Join Match'
            : 'Soon';
        const buttonClass = [
          'rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-wide transition',
          disableButton
            ? 'cursor-not-allowed border border-white/10 bg-white/10 opacity-40'
            : 'border border-transparent bg-neon-500 text-solana-900 shadow-[0_0_30px_rgba(92,225,230,0.35)] hover:bg-neon-400',
          joiningThisMatch ? 'animate-pulse' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <motion.li
            key={match.matchId}
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="arcade-panel relative overflow-hidden rounded-xl p-5"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-semibold text-neon-300">
                    {currencyLabel[match.wager.currency]}{' '}
                    {formatLamports(
                      normalizeLamports(match.wager.amountLamports as unknown),
                      match.wager.currency,
                    )}
                  </p>
                  <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-white/60">
                    {statusLabel}
                  </span>
                </div>
                <p className="text-xs text-white/50">Expires {expiresLabel}</p>
                <p className="text-[11px] uppercase tracking-wide text-neon-300/80">
                  Stakes Locked: {locked}/{total}
                </p>
                <p className="text-[11px] text-white/50">{walletSummary}</p>
              </div>
              <button
                type="button"
                onClick={() => onJoin?.(match)}
                disabled={disableButton}
                className={buttonClass}
              >
                {joinLabel}
              </button>
            </div>
          </motion.li>
        );
      })}
    </ul>
  );
};
